import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { transactionAPI } from '../services/api';
import { Search, Plus, Edit, Trash2, Calendar, DollarSign, Filter, X } from 'lucide-react';
import { hasPermission } from '../utils/permissions';
import { formatCurrency, formatDate } from '../utils/formatters';

const ManagementFees = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  
  const [formData, setFormData] = useState({
    transaction_id: '',
    member_number: '',
    fee_amount: '',
    payment_date: '',
    next_payment_date: '',
    status: 'pending',
    memo: ''
  });

  // 관리비 목록 조회
  const { data: managementFees, isLoading } = useQuery({
    queryKey: ['managementFees', currentPage, searchTerm, selectedStatus],
    queryFn: () => transactionAPI.getManagementFees({
      page: currentPage,
      limit: 10,
      search: searchTerm,
      status: selectedStatus
    }),
    select: (response) => {
      // API 응답 구조를 안전하게 처리
      const data = response?.data || {};
      return {
        data: Array.isArray(data.management_fees) ? data.management_fees : [],
        pagination: data.pagination || {
          total: 0,
          current_page: 1,
          last_page: 1,
          from: 0,
          to: 0
        }
      };
    },
    keepPreviousData: true
  });

  // 관리비 추가/수정 뮤테이션
  const mutation = useMutation({
    mutationFn: (data) => {
      if (editingFee) {
        return transactionAPI.updateManagementFee(editingFee.id, data);
      } else {
        return transactionAPI.createManagementFee(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['managementFees']);
      success(editingFee ? '관리비가 수정되었습니다.' : '관리비가 등록되었습니다.');
      setShowAddModal(false);
      setEditingFee(null);
      resetForm();
    },
    onError: (error) => {
      showError(error.message || '오류가 발생했습니다.');
    }
  });

  const resetForm = () => {
    setFormData({
      transaction_id: '',
      member_number: '',
      fee_amount: '',
      payment_date: '',
      next_payment_date: '',
      status: 'pending',
      memo: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  const handleEdit = (fee) => {
    setEditingFee(fee);
    setFormData({
      transaction_id: fee.transaction_id || '',
      member_number: fee.member_number || '',
      fee_amount: fee.fee_amount || '',
      payment_date: fee.payment_date || '',
      next_payment_date: fee.next_payment_date || '',
      status: fee.status || 'pending',
      memo: fee.memo || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      transactionAPI.deleteManagementFee(id).then(() => {
        queryClient.invalidateQueries(['managementFees']);
        success('관리비가 삭제되었습니다.');
      }).catch((error) => {
        showError(error.message || '삭제 중 오류가 발생했습니다.');
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'badge-success';
      case 'pending': return 'badge-warning';
      case 'overdue': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'paid': return '납부완료';
      case 'pending': return '납부대기';
      case 'overdue': return '납부지연';
      default: return '알 수 없음';
    }
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">관리비 관리</h1>
          <p className="text-gray-600 mt-1">묘지 관리비 납부 현황을 관리하세요</p>
        </div>
        {hasPermission(user?.role, 'CONTRACT_EDIT') && (
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary"
          >
            <Plus size={20} />
            관리비 등록
          </button>
        )}
      </div>

      {/* 검색 및 필터 */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="form-label">검색</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="묘적번호로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            <div>
              <label className="form-label">상태</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="select"
              >
                <option value="">전체 상태</option>
                <option value="paid">납부완료</option>
                <option value="pending">미납</option>
                <option value="overdue">연체</option>
              </select>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  // 검색 실행 로직
                }}
                className="btn btn-primary"
              >
                <Filter size={16} className="mr-2" />
                조회
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 관리비 목록 테이블 */}
      <div className="card">
        <div className="card-header">
          <div className="flex-between">
            <h2 className="text-lg font-semibold">관리비 목록</h2>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              {/* <Filter size={16} /> */}
            </div>
          </div>
        </div>
        
        <div className="card-body">
          {isLoading ? (
            <div className="loading">로딩 중...</div>
          ) : !managementFees?.data || managementFees.data.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <DollarSign size={48} className="mx-auto mb-4 text-gray-300" />
              <p>관리비 내역이 없습니다.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>묘적번호</th>
                      <th>관리비 금액</th>
                      <th>납부일</th>
                      <th>다음 납부일</th>
                      <th>상태</th>
                      <th>메모</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managementFees.data.map((fee) => (
                      <tr key={fee.id}>
                        <td className="font-medium">{fee.member_number}</td>
                        <td>{formatCurrency(fee.fee_amount || 0)}</td>
                        <td>{formatDate(fee.payment_date)}</td>
                        <td>{formatDate(fee.next_payment_date)}</td>
                        <td>
                          <span className={`badge ${getStatusColor(fee.status)}`}>
                            {getStatusText(fee.status)}
                          </span>
                        </td>
                        <td className="max-w-xs truncate">{fee.memo}</td>
                        <td>
                          <div className="flex items-center space-x-2">
                            {hasPermission(user?.role, 'CONTRACT_EDIT') && (
                              <button
                                onClick={() => handleEdit(fee)}
                                className="text-blue-600 hover:text-blue-800"
                                title="수정"
                              >
                                <Edit size={16} />
                              </button>
                            )}
                            {hasPermission(user?.role, 'CONTRACT_DELETE') && (
                              <button
                                onClick={() => handleDelete(fee.id)}
                                className="text-red-600 hover:text-red-800"
                                title="삭제"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 페이지네이션 */}
              {managementFees?.pagination && managementFees.pagination.last_page > 1 && (
                <div className="card-footer">
                  <div className="pagination">
                    <div className="pagination-info">
                      {/* 총 {managementFees.pagination.total}건 중 {managementFees.pagination.from}-
                      {managementFees.pagination.to}건 */}
              <span>총 {managementFees?.pagination?.last_page ||1} 페이지</span>

                    </div>
                    <div className="pagination-nav">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="pagination-button"
                      >
                        이전
                      </button>
                      <span className="px-3 py-1 text-sm text-gray-700">
                        {currentPage} / {managementFees.pagination.last_page}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={currentPage >= managementFees.pagination.last_page}
                        className="pagination-button"
                      >
                        다음
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 관리비 등록/수정 모달 */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="flex-between mb-4">
              <h2 className="text-xl font-bold">
                {editingFee ? '관리비 수정' : '관리비 등록'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingFee(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">묘적번호</label>
                  <input
                    type="text"
                    value={formData.member_number}
                    onChange={(e) => setFormData({...formData, member_number: e.target.value})}
                    className="input"
                    placeholder="묘적번호를 입력하세요"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">관리비 금액</label>
                  <input
                    type="number"
                    value={formData.fee_amount}
                    onChange={(e) => setFormData({...formData, fee_amount: parseInt(e.target.value) || 0})}
                    className="input"
                    placeholder="금액을 입력하세요"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">납부일</label>
                  <input
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">다음 납부일</label>
                  <input
                    type="date"
                    value={formData.next_payment_date}
                    onChange={(e) => setFormData({...formData, next_payment_date: e.target.value})}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">상태</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="select"
                  >
                    <option value="pending">납부대기</option>
                    <option value="paid">납부완료</option>
                    <option value="overdue">납부지연</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="form-label">메모</label>
                <textarea
                  value={formData.memo}
                  onChange={(e) => setFormData({...formData, memo: e.target.value})}
                  className="input"
                  rows="3"
                  placeholder="메모를 입력하세요"
                />
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingFee(null);
                    resetForm();
                  }}
                  className="btn btn-outline"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={mutation.isLoading}
                  className="btn btn-primary"
                >
                  {mutation.isLoading ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagementFees; 